import { useFormik } from 'formik';
import { useCallback } from 'react';
import * as Yup from 'yup';
import type { DetailsFormFields } from '../types';
import { useTranslation } from '@shared/core';

export const useDetailsFormFields = (initialValues: DetailsFormFields, handleFormSubmit: (details: DetailsFormFields) => void) => {
  const { t: translateForm } = useTranslation('generalFormValidation');
  const { t2 } = useTranslation('sharedRegistry');
  const { validEmailError } = t2('shoppingCart', 'detailsStep', 'detailsForm');

  const formik = useFormik<DetailsFormFields>({
    enableReinitialize: true,
    // Without this, `errors` is empty until something changes, so `isValid` reports true
    // for a blank form and callers have to fall back to `dirty` to mean "filled in".
    // That proxy breaks the moment details arrive prefilled rather than typed, so validate
    // up front and let `isValid` answer the question directly. Errors stay invisible until
    // a field is touched — the form reads `touched && errors`.
    validateOnMount: true,
    initialValues,
    onSubmit: handleFormSubmit,
    validationSchema: Yup.object<DetailsFormFields>({
      name: Yup.string().required(translateForm('required')),
      email: Yup.string().email(validEmailError).required(translateForm('required')),
      message: Yup.string(),
    }),
  });

  const getFormFieldError = useCallback(
    (fieldName: keyof DetailsFormFields) => {
      const { errors, touched } = formik;
      return touched[fieldName] && errors[fieldName] ? errors[fieldName] : undefined;
    },
    [formik],
  );

  return {
    formik,
    getFieldProps: formik.getFieldProps,
    errors: {
      nameError: getFormFieldError('name'),
      emailError: getFormFieldError('email'),
    },
    isFormValid: formik.isValid,
    saveDetails: formik.submitForm,
  };
};
